import {isCopilot} from '@github-ui/assignees/copilot-user'
import {validateIssueTitle} from '@github-ui/entity-validators'
import {useIssueCreateActions, useIssueCreateMetadata} from '@github-ui/issue-create/IssueCreateDataContext'
import {instanceOfIssueFormData, type IssueCreatePayload} from '@github-ui/issue-create/Model'
import type {IssueFormRef} from '@github-ui/issue-form/Types'
import type {RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import {Button} from '@primer/react'
import React, {type RefObject, useCallback, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import type {DraftIssue} from '../content-preview-types'
import {commitUpdateIssueMutation, type OnUpdateProps} from './update-issue-mutation'
import {fetchIssue} from './use-issue-query'
import type {SavedIssue} from './use-on-save-callback'
import {useTransformCopilotImageURLsFunction} from './use-transform-copilot-image-urls-function'

export type UpdateIssueButtonProps = {
  issueRepo: RepositoryPickerRepository
  draftIssue: DraftIssue
  template?: IssueCreatePayload
  issueFormRef: RefObject<IssueFormRef | null>
  onUpdate?: (issue: SavedIssue) => void
  onUpdateError?: (error: Error | OnUpdateProps) => void
  onClose: () => void
}

export function UpdateIssueButton({
  issueRepo,
  draftIssue,
  template,
  issueFormRef,
  onUpdate,
  onUpdateError,
  onClose,
}: UpdateIssueButtonProps) {
  const environment = useRelayEnvironment()
  const [isUpdating, setIsUpdating] = useState(false)

  const {assignees, labels, milestone, issueType, projects} = useIssueCreateMetadata()
  const {clearSessionData} = useIssueCreateActions()

  // Apply permission filtering to the metadata before sending to mutation
  const getPermissionFilteredMetadata = useCallback(() => {
    const permissions = issueRepo.viewerIssueCreationPermissions

    return {
      // Null values mean the value is being unset
      // Undefined values mean the value is not being changed
      assignees: permissions?.assignable ? assignees : undefined,
      labels: permissions?.labelable ? labels : undefined,
      milestone: permissions?.milestoneable ? milestone : undefined,
      issueType: permissions?.typeable ? issueType || null : undefined,
      projects: permissions?.triageable ? projects : undefined,
      hasInaccessibleProject: permissions?.triageable ? draftIssue.rawProjectsInput?.includes('') : undefined,
    }
  }, [
    assignees,
    draftIssue.rawProjectsInput,
    issueRepo.viewerIssueCreationPermissions,
    issueType,
    labels,
    milestone,
    projects,
  ])

  const onClearSession = useCallback(() => {
    clearSessionData()
    issueFormRef.current?.clearSessionStorage()
  }, [clearSessionData, issueFormRef])

  const clearOnSave = useCallback(() => {
    onClearSession()
    onClose?.()
  }, [onClearSession, onClose])

  const transformCopilotImageUrls = useTransformCopilotImageURLsFunction()

  const handleUpdate = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()

      setIsUpdating(true)

      try {
        // Instead of using the existingIssue from the Preview, we opt to fetch
        // the current issue state right before the mutation. This ensures we are not
        // operating against stale data, and apply the state the user expects.
        const [owner, repo] = draftIssue.repository?.split('/') ?? []
        if (!owner || !repo || !draftIssue.issueNumber) {
          const error = new Error('Invalid repository information')
          error.name = 'OTHER'
          onUpdateError?.(error)
          setIsUpdating(false)
          return
        }

        // Fetch the current issue state from the network to ensure we have the latest data
        const currentIssue = await fetchIssue(
          environment,
          owner,
          repo,
          draftIssue.issueNumber,
          true, // forceNetwork = true to bypass cache
        )

        if (!currentIssue) {
          const error = new Error('Issue not found or no longer accessible')
          error.name = 'ISSUE_NOT_FOUND'
          onUpdateError?.(error)
          setIsUpdating(false)
          return
        }

        // Since we can't submit directly through CreateIssueForm, we need to validate the inputs manually
        // TODO: we need a better way to handle validation
        const inputValidationResult = validateIssueTitle(draftIssue.name)
        if (!inputValidationResult.isValid) {
          const error = new Error(inputValidationResult.errorMessage || 'Invalid title')
          error.name = 'INVALID_TITLE'
          onUpdateError?.(error)
          return
        }

        let issueBodyToSave = draftIssue.body ?? ''

        const isIssueForm = template && instanceOfIssueFormData(template.data)
        if (isIssueForm && issueFormRef.current) {
          const invalidInputs = issueFormRef.current.getInvalidInputs()
          if (invalidInputs.length > 0) {
            const error = new Error('Please fix the highlighted errors in the form.')
            error.name = 'INVALID_FORM_INPUTS'
            onUpdateError?.(error)
            invalidInputs[0]?.focus()
            return
          } else {
            issueBodyToSave = issueFormRef.current.markdown() ?? ''
          }
        }

        issueBodyToSave = await transformCopilotImageUrls(issueRepo, issueBodyToSave)

        const currentAssignees =
          currentIssue.assignees?.nodes?.filter(
            (assignee): assignee is NonNullable<typeof assignee> => assignee != null,
          ) ?? []
        const isCopilotCurrentlyAssigned = currentAssignees.some(assignee => isCopilot(assignee.login))
        const viewerCanAssign = issueRepo.viewerIssueCreationPermissions?.assignable ?? false
        const canUseCopilotAssignment =
          viewerCanAssign &&
          !currentIssue.repository?.isArchived &&
          !!currentIssue.repository?.isCopilotAgentEnabled &&
          !isCopilotCurrentlyAssigned

        // Get permission-filtered metadata to respect user permissions
        const filteredMetadata = getPermissionFilteredMetadata()
        // Prepare fields that need special processing in the button
        const proposedChanges = {
          body: issueBodyToSave,
          assignees: filteredMetadata.assignees,
          labels: filteredMetadata.labels,
          milestone: filteredMetadata.milestone,
          issueType: filteredMetadata.issueType,
          projects: filteredMetadata.projects,
          hasInaccessibleProject: filteredMetadata.hasInaccessibleProject,
        }

        // Use the new reworked mutation that compares current vs draft
        await commitUpdateIssueMutation({
          environment,
          currentIssue, // Use the fresh issue data
          draftIssue,
          proposedChanges,
          canUseCopilotAssignment,
          onCompleted: (onUpdateProps: OnUpdateProps) => {
            if (onUpdateProps.result.failed.length > 0) {
              // Any errors: pass OnUpdateProps to onUpdateError for handling
              onUpdateError?.(onUpdateProps)
            } else if (onUpdateProps.result.successful.length > 0 && onUpdateProps.result.issue) {
              // No errors: use onUpdate with converted issue
              clearOnSave()
              onClose()

              // Pass SavedIssue directly to the callback
              onUpdate?.(onUpdateProps.result.issue)
            } else {
              // No operations performed (likely no changes detected)
              clearOnSave()
              onClose()
              // Don't call onUpdate since nothing changed
            }
          },
        })
      } catch {
        // Mutation errors are handled by the onError callback
        const error = new Error('Update failed')
        error.name = 'OTHER'
        onUpdateError?.(error)
      } finally {
        setIsUpdating(false)
      }
    },
    [
      clearOnSave,
      draftIssue,
      environment,
      getPermissionFilteredMetadata,
      issueFormRef,
      issueRepo,
      onClose,
      onUpdate,
      onUpdateError,
      template,
      transformCopilotImageUrls,
    ],
  )

  return (
    <>
      <Button
        variant="primary"
        data-testid="update-issue-button"
        onClick={handleUpdate}
        disabled={isUpdating || !draftIssue.bodyVersion}
      >
        {isUpdating ? 'Updating...' : 'Update'}
      </Button>
    </>
  )
}
